// One judge for every studio fix: the plan as it will be DRAWN, graded the way the
// planogram's own Aesthetics tile grades it. A fix measured on anything else can report a
// gain the screen shows as a loss.
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { pogToStudioItems } from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import {
  gradedLevelsOf,
  type AestheticsRules,
} from "@CommonUtils/cdtStudio/domain/aestheticsRules";
import { isSizeOnlyTier } from "@CommonUtils/cdtStudio/domain/aestheticsScore";
import { scoreBlockShape } from "@CommonUtils/cdtStudio/domain/blockShape";
import {
  hierarchyFromColumns,
  pogAestheticsScore,
} from "@CommonUtils/cdtStudio/domain/pogAesthetics";
import { layoutToPog } from "../adapters/layoutToPog";
import { buildLayout } from "./buildLayout";
import { closeFillGap } from "./cdtLayout/closeFillGap";
import { fillShortRows, SHELVES_FULL_GATE, type FillPlan } from "./fillShortRows";
import { namesNoBlock } from "./plainNames";
import type { GeneratedLayout, StudioDraft, StudioItem, StudioRunGeometry } from "./studioTypes";
import { slabBlockKeyOf } from "./verticalBlockingFix";

export interface PlanVerdict {
  /** The tile's score, or NaN for a plan it cannot grade. */
  score: number;
  /** The same score before it is rounded, which is what a repair is judged on. */
  exactScore: number;
  /** Ids of the checks the plan does not pass. */
  failedGates: readonly string[];
  /** Top-level blocks the plan has in pieces. A check already failed can still fail worse,
   *  and a fix that leaves more blocks in pieces than it found is no repair. */
  blocksInPieces?: number;
  /** Every one of those blocks, by the name the tile gives it (see `blocksInPiecesOf`), read
   *  once and only when asked; null where that reading does not count as many as the tile. */
  blocksNamedInPieces?: () => readonly string[] | null;
  /** The products the plan puts on a shelf, by product code. */
  placed?: ReadonlySet<string>;
  /** The most of a shelf row's width the "Shelves full" check lets stand empty, 0-1. */
  maxRowAir?: number;
}

const placedOf = (pog: Pog): Set<string> =>
  new Set(
    pog.planogram.bays.flatMap((bay) =>
      bay.shelves.flatMap((shelf) => shelf.items.map((item) => item.productCode || item.id)),
    ),
  );

/**
 * Every block the tile's "every top-level block in one piece" check counts, by name. The tile
 * names at most three, so all are read here with the tile's own shape measure, on the plan as
 * the tile reads it: its CDT columns, a placeholder value naming no block, its graded levels,
 * its shelves as they stand in the bays, and its stretches in its order.
 */
const blocksInPiecesOf = (pog: Pog, rules: AestheticsRules): string[] => {
  const { items, runs } = pogToStudioItems([pog]);
  const { levels: columns } = hierarchyFromColumns(items);
  const blockValue = (value: string | null | undefined) => {
    const trimmed = value?.trim() ?? "";
    return namesNoBlock(trimmed) ? null : trimmed;
  };
  const stretches = items.flatMap((item) => {
    const attrs = { ...item.attrs };
    for (const attr of columns) attrs[attr] = blockValue(item.attrs[attr]);
    return item.placements
      .filter((placement) => placement.widthCm > 0)
      .map((placement) => ({
        run: placement.run,
        shelf: placement.shelf,
        x: placement.xCm,
        width: placement.widthCm,
        packWidth:
          item.facingWidthCm > 0
            ? item.facingWidthCm
            : placement.widthCm / Math.max(1, placement.hFacings),
        attrs,
        sku: item.skuId,
      }));
  });
  stretches.sort(
    (a, b) =>
      a.run - b.run ||
      a.shelf - b.shelf ||
      a.x - b.x ||
      (a.sku < b.sku ? -1 : a.sku > b.sku ? 1 : 0),
  );
  const shape = scoreBlockShape(
    stretches,
    gradedLevelsOf(rules).map(({ attr, weight }) => ({ attr, weight })),
    {
      pieceCost: rules.blockPieceCost,
      interleaveFactor: rules.contiguityInterleaveFactor,
      fillFloor: rules.blockFillFloor,
      jitterPacks: rules.jitterPacks,
      skipUnvalued: true,
    },
    runs.map((run) => ({
      run: run.run,
      widthCm: run.widthCm,
      rows: run.shelves.map((shelf) => ({
        rank: shelf.rank,
        spans: shelf.spans?.length
          ? shelf.spans.map((span) => ({ from: span.xCm, to: span.xCm + span.widthCm }))
          : [{ from: 0, to: run.widthCm }],
      })),
    })),
  );
  return shape.broken
    .filter(
      (block) =>
        block.depth < rules.approvalLevels &&
        (block.pieces > 1 || block.extraRuns > 0) &&
        !isSizeOnlyTier(rules, block),
    )
    .map((block) => block.block);
};

/** Each plan's blocks in pieces, read once (see `blocksInPiecesOf`). */
const namedInPieces = new WeakMap<Pog, readonly string[] | null>();

export const verdictOfPog = (pog: Pog | null): PlanVerdict => {
  const graded = pogAestheticsScore(pog);
  if (!graded || !pog) return { score: Number.NaN, exactScore: Number.NaN, failedGates: [] };
  const count = graded.gates.find((gate) => gate.id === "block-in-pieces")?.count ?? 0;
  const blocksNamedInPieces = () => {
    if (!namedInPieces.has(pog)) {
      const named = blocksInPiecesOf(pog, graded.rules);
      if (named.length !== count) {
        console.warn(
          `Blocks in pieces: the tile counts ${count}, the studio reads ${named.length}; judged by the count alone.`,
        );
      }
      namedInPieces.set(pog, named.length === count ? named : null);
    }
    return namedInPieces.get(pog) ?? null;
  };
  return {
    score: graded.score,
    exactScore: graded.exactScore,
    failedGates: graded.gates
      .filter((gate) => gate.evaluated && !gate.passed)
      .map((gate) => gate.id),
    blocksInPieces: count,
    blocksNamedInPieces,
    placed: placedOf(pog),
    maxRowAir: 1 - graded.rules.approvalFullness,
  };
};

/** A block the candidate has in pieces that the incumbent keeps whole. Mending one block by
 *  breaking another leaves the count as it was, so the blocks are told apart by name, read
 *  only here, where the count alone does not decide. Where either reading disagrees with the
 *  tile's count, the count alone judges. */
const breaksAnotherBlock = (candidate: PlanVerdict, incumbent: PlanVerdict): boolean => {
  if (!candidate.blocksInPieces || !candidate.blocksNamedInPieces) return false;
  const mine = candidate.blocksNamedInPieces();
  const theirs = incumbent.blocksNamedInPieces ? incumbent.blocksNamedInPieces() : [];
  if (!mine || !theirs) return false;
  return mine.some((block) => !theirs.includes(block));
};

/** A product the incumbent puts on a shelf that the candidate puts nowhere. */
const shedsAProduct = (candidate: PlanVerdict, incumbent: PlanVerdict): boolean => {
  const placed = candidate.placed;
  return !!placed && [...(incumbent.placed ?? [])].some((code) => !placed.has(code));
};

/** Fails a check the incumbent passes, or leaves more blocks in pieces than it found, or a
 *  block in pieces it did not, or sheds a product it places. */
const doesHarm = (candidate: PlanVerdict, incumbent: PlanVerdict): boolean =>
  candidate.failedGates.some((gate) => !incumbent.failedGates.includes(gate)) ||
  (candidate.blocksInPieces ?? 0) > (incumbent.blocksInPieces ?? Infinity) ||
  breaksAnotherBlock(candidate, incumbent) ||
  shedsAProduct(candidate, incumbent);

/** A layout stamped over the plan the studio opened on, as the studio draws it: what that plan
 *  ranged and the layout no longer places is listed as unranged, so the range checks see it go
 *  (a product with no place would otherwise vanish from the plan). */
export const stampLayout = (
  basePog: Pog,
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  unranged: readonly string[] = [],
): Pog => {
  const dropped = basePog.planogram.bays
    .flatMap((bay) => bay.shelves.flatMap((shelf) => shelf.items))
    .map((item) => item.productCode || item.id)
    .filter((skuId) => !layout.placements[skuId]?.segments.length);
  return layoutToPog(basePog, layout, items, 0, [...new Set([...unranged, ...dropped])]);
};

/** Each shelf's products in order, with their facings; neighbouring positions of one product
 *  read as one. */
const placementsOf = (pog: Pog): string[] =>
  pog.planogram.bays
    .flatMap((bay) =>
      bay.shelves.map((shelf) => {
        const positions: [string, number][] = [];
        for (const item of shelf.items) {
          const code = item.productCode || item.id;
          const last = positions[positions.length - 1];
          if (last?.[0] === code) last[1] += item.facings;
          else positions.push([code, item.facings]);
        }
        return JSON.stringify([shelf.uniqueId, positions]);
      }),
    )
    .sort();

/** Both plans put every product on the same shelf, in the same order, with the same facings. */
export const samePlacements = (left: Pog, right: Pog): boolean => {
  const a = placementsOf(left);
  const b = placementsOf(right);
  return a.length === b.length && a.every((shelf, index) => shelf === b[index]);
};

/** `stamped` is the layout already stamped (`stampLayout`), where the caller has it. */
export const verdictOfLayout = (
  basePog: Pog,
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  stamped: Pog = stampLayout(basePog, layout, items),
): PlanVerdict => verdictOfPog(stamped);

/** Strictly better, and never by way of harm (see `doesHarm`). An ungradable
 * candidate is never better; an ungradable incumbent is beaten by anything gradable, even a
 * plan that fails a check: it names no check of its own to fail. */
export const improvesOn = (candidate: PlanVerdict, incumbent: PlanVerdict): boolean => {
  if (Number.isNaN(candidate.score)) return false;
  if (Number.isNaN(incumbent.score)) return true;
  if (doesHarm(candidate, incumbent)) return false;
  return candidate.exactScore > incumbent.exactScore;
};

/** Better, and by a score the Aesthetics tile shows: its rounded score rises. Every fix the
 *  studio offers does this, or a click shows nothing gained. A plan the tile cannot grade is
 *  raised by any plan it can. */
export const raisesTile = (candidate: PlanVerdict, incumbent: PlanVerdict): boolean =>
  improvesOn(candidate, incumbent) &&
  (Number.isNaN(incumbent.score) || Math.round(candidate.score) > Math.round(incumbent.score));

/** Does no harm: no lower score before rounding, no newly failed check, no more blocks in
 *  pieces. An ungradable candidate never holds, not even against an ungradable incumbent. */
export const holdsAgainst = (candidate: PlanVerdict, incumbent: PlanVerdict): boolean => {
  if (Number.isNaN(candidate.score)) return false;
  if (Number.isNaN(incumbent.score)) return true;
  return !doesHarm(candidate, incumbent) && candidate.exactScore >= incumbent.exactScore;
};

/** Holds on the score the tile shows: no harm, and no lower rounded score. A step on a path
 *  judged as a whole (one facing of a fill) may dip below its start before rounding, so long
 *  as the path's end is judged on `holdsAgainst` or `improvesOn`. */
export const holdsOnTile = (candidate: PlanVerdict, incumbent: PlanVerdict): boolean => {
  if (Number.isNaN(candidate.score)) return false;
  if (Number.isNaN(incumbent.score)) return true;
  return !doesHarm(candidate, incumbent) && candidate.score >= incumbent.score;
};

/** A tree edit is the user's call, refused only for harm (`doesHarm`), or for laying a plan
 *  the tile cannot grade where it grades the plan now. A lower score is theirs to accept. */
export const treeEditHarms = (laid: PlanVerdict, standing: PlanVerdict): boolean =>
  Number.isNaN(laid.score)
    ? !Number.isNaN(standing.score)
    : !Number.isNaN(standing.score) && doesHarm(laid, standing);

/**
 * Every product the plan puts on a shelf it did not stand on in `before` fits under that
 * shelf, stacked and capped as it is drawn. The measure has no height, so a move that stands
 * a pack on a shelf too low for it would otherwise pass as a repair.
 */
export const fitsWhereMoved = (before: Pog, after: Pog): boolean => {
  const shelvesOf = (plan: Pog) => {
    const shelves = new Map<string, Set<string>>();
    for (const bay of plan.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          const sku = item.productCode || item.id;
          shelves.set(sku, (shelves.get(sku) ?? new Set()).add(shelf.uniqueId));
        }
      }
    }
    return shelves;
  };
  const stood = shelvesOf(before);
  for (const bay of after.planogram.bays) {
    for (const shelf of bay.shelves) {
      const clearance = shelf.getHeight() - shelf.thickness;
      for (const item of shelf.items) {
        if (stood.get(item.productCode || item.id)?.has(shelf.uniqueId)) continue;
        const drawn = item.getHeight() * item.getVerticalFacings() + item.getYCappingHeight();
        if (drawn > clearance + 1e-6) return false;
      }
    }
  }
  return true;
};

/** The plan's products as drawn, and its verdict, for the row fill. */
const fillPlanOf = (pog: Pog): FillPlan => ({
  pog,
  items: pogToStudioItems([pog]).items,
  verdict: verdictOfPog(pog),
});

/** The draft with its rows filled the way `fillShortRows` fills the plan it lays: every
 *  product pinned to the shelves and facings of the filled plan. Null where nothing was added. */
const rowsFilledDraftOf = (
  basePog: Pog,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  stamped: Pog,
): StudioDraft | null => {
  const blockOf = new Map(items.map((item) => [item.skuId, slabBlockKeyOf(draft, item)]));
  const fill = fillShortRows(fillPlanOf(stamped), {
    runs,
    read: fillPlanOf,
    stamp: (layout, drawn) => stampLayout(basePog, layout, drawn),
    blockOf: (skuId) => blockOf.get(skuId) ?? skuId,
    holds: holdsAgainst,
  });
  if (fill.grown.length === 0) return null;
  const shelfHints: StudioDraft["shelfHints"] = Object.fromEntries(
    fill.end.items
      .filter((item) => item.placements.length > 0)
      .map((item) => [
        item.skuId,
        item.placements.map((placement) => ({
          shelf: placement.shelf,
          hFacings: placement.hFacings,
        })),
      ]),
  );
  return { ...draft, shelfHints };
};

/**
 * A moved tree can regenerate with a shelf left short. Where the drawn plan fails the
 * full-shelves check, the short rows are filled with facings of the products already on them
 * (`fillShortRows`), and the fill gap is closed the older way (`closeFillGap`); the better of
 * the two is kept where it betters the drawn plan, or clears the check at no harm. The draft
 * comes back with the verdict of the plan it lays, so the caller need not grade it again.
 */
export const closeFillWhereShort = (
  basePog: Pog,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  fillFloorShare: number,
): { draft: StudioDraft; laid: PlanVerdict } => {
  const stamped = stampLayout(basePog, buildLayout(items, draft, runs), items);
  const plain = verdictOfPog(stamped);
  if (!plain.failedGates.includes(SHELVES_FULL_GATE)) return { draft, laid: plain };
  const better = (laid: PlanVerdict) =>
    improvesOn(laid, plain) ||
    (holdsAgainst(laid, plain) && !laid.failedGates.includes(SHELVES_FULL_GATE));
  let best: { draft: StudioDraft; laid: PlanVerdict } = { draft, laid: plain };
  const consider = (next: () => StudioDraft | null) => {
    try {
      const candidate = next();
      if (!candidate) return;
      // Graded as the candidate draft lays, which is the plan the caller lays.
      const laid = verdictOfLayout(basePog, buildLayout(items, candidate, runs), items);
      if (better(laid) && (best.draft === draft || improvesOn(laid, best.laid))) {
        best = { draft: candidate, laid };
      }
    } catch {
      // A fill that cannot be laid is no fill.
    }
  };
  consider(() => rowsFilledDraftOf(basePog, items, runs, draft, stamped));
  consider(() => closeFillGap(items, runs, draft, fillFloorShare).draft);
  return best;
};
